import { createSelector } from '@reduxjs/toolkit';

import { StreamingPlatform } from 'types';
import { convertDateToTimestamp, diffTimestamps } from 'utils';
import { RootState } from 'store/reducers';
import { StreamingPlatformsState, StreamingPlatformState } from 'store/slices/streamingPlatforms';

export const streamingPlatformSelector = (platformKey: StreamingPlatform) =>
  createSelector<RootState, StreamingPlatformState, StreamingPlatformState>(
    state => state.streamingPlatforms[platformKey],
    streamingPlatformState => streamingPlatformState
  );

export const streamingPlatformsSelector = createSelector<RootState, StreamingPlatformsState, StreamingPlatformState[]>(
  state => state.streamingPlatforms,
  streamingPlatforms => Object.keys(streamingPlatforms).map(key => streamingPlatforms[key])
);

export const enabledStreamingPlatformsSelector = createSelector<
  RootState,
  StreamingPlatformState[],
  StreamingPlatformState[]
>(
  state => streamingPlatformsSelector(state),
  streamingPlatforms => streamingPlatforms.filter(platform => platform.enabled)
);

export const activeStreamingPlatformsSelector = createSelector<
  RootState,
  StreamingPlatformState[],
  StreamingPlatformState[]
>(
  state => enabledStreamingPlatformsSelector(state),
  enabledPlatforms => enabledPlatforms.filter(platform => platform.isActive)
);

export const linkedStreamingPlatformsSelector = createSelector<
  RootState,
  StreamingPlatformState[],
  StreamingPlatformState[]
>(
  state => enabledStreamingPlatformsSelector(state),
  enabledPlatforms => enabledPlatforms.filter(platform => platform.isLinked)
);

export const notLinkedStreamingPlatformsSelector = createSelector<
  RootState,
  StreamingPlatformState[],
  StreamingPlatformState[]
>(
  state => enabledStreamingPlatformsSelector(state),
  enabledPlatforms => enabledPlatforms.filter(platform => !platform.isLinked)
);

export const expiredStreamingPlatformsSelector = createSelector<
  RootState,
  StreamingPlatformState[],
  StreamingPlatformState[]
>(
  state => linkedStreamingPlatformsSelector(state),
  linkedPlatforms =>
    linkedPlatforms.filter(linkedPlatform => {
      const nowTs = convertDateToTimestamp(new Date());

      if (linkedPlatform.expiresAt) {
        const tsDiff = diffTimestamps(nowTs, linkedPlatform.expiresAt);

        return tsDiff < 10_000;
      }

      return true;
    })
);
